import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { useFormattingLocale } from '../../utils/getFormattingLocales';
import { FormLabel } from '../formlabel/FormLabel';
import {
  monthToIsoEnd,
  parseIsoPeriod,
  quarterToIsoEnd,
} from '../period-picker/periodRange';
import { SegmentedControl } from '../segmentedControl/SegmentedControl';
import { SingleSelect } from '../selects/SingleSelect';

import styles from './month-quarter-picker.module.scss';

export type MonthQuarterPickerMode = 'month' | 'quarter';

type MonthSelectionState = { month: number | null; year: number | null };
type QuarterSelectionState = { quarter: number | null; year: number | null };
type YearOption = { label: string; value: string };

export type MonthQuarterPickerProps = {
  /** ISO 8601 value (YYYY-MM-DD). When set, the picker is controlled. */
  value?: string | null;
  /** Optional ISO 8601 initial value (YYYY-MM-DD). */
  defaultValue?: string;
  /** Called with the last day of the selected period as an ISO 8601 string.
   *  Fires with null when a complete selection is cleared. */
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  /** When true (default), a clear button is shown allowing the selection to be
   *  reset to an empty state. */
  clearable?: boolean;
  /** Defaults the year select to the current year when no value is provided. */
  defaultYearToCurrent?: boolean;
  /** When true, select dropdowns render inside a portal (default).
   *  Set to false in Storybook or contexts where portals cause layout issues. */
  withinPortal?: boolean;
  description?: ReactNode | ReactNode[];
  label?: ReactNode | ReactNode[];
  required?: boolean;
  /** String error message is displayed below the selects. Boolean true marks
   *  the label as errored without showing a message. */
  error?: string | boolean;
  /** Override year options displayed by the year select. Defaults to current year ± 5. */
  yearOptions?: YearOption[];
  /**
   * Inclusive maximum selectable period end date as YYYY-MM-DD.
   * Periods whose month/quarter end is after this value are unavailable.
   * That comparison uses the period end because this picker always emits the
   * last day of the selected month/quarter as the stored date value.
   */
  maxDate?: string;
  className?: string;
  'data-testid'?: string;
};

const QUARTER_OPTIONS = [
  { label: 'Q1', value: '1' },
  { label: 'Q2', value: '2' },
  { label: 'Q3', value: '3' },
  { label: 'Q4', value: '4' },
];

const MONTH_NUMBERS = Array.from({ length: 12 }, (_, i) => i + 1);

const DEFAULT_YEAR_OPTIONS: YearOption[] = (() => {
  const center = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, i) => {
    const y = center + 5 - i;
    return { label: String(y), value: String(y) };
  });
})();

function quarterFromMonth(month: number): number {
  return Math.ceil(month / 3);
}

function isPeriodWithinMaxDate(isoValue: string, maxDate?: string) {
  return maxDate == null || isoValue <= maxDate;
}

function isMonthWithinMaxDate(year: number, month: number, maxDate?: string) {
  return isPeriodWithinMaxDate(monthToIsoEnd(month, year), maxDate);
}

function isQuarterWithinMaxDate(
  year: number,
  quarter: number,
  maxDate?: string
) {
  return isPeriodWithinMaxDate(quarterToIsoEnd(quarter, year), maxDate);
}

function isYearWithinMaxDate(
  year: number,
  mode: MonthQuarterPickerMode,
  maxDate?: string
) {
  if (maxDate == null) {
    return true;
  }

  if (mode === 'month') {
    return MONTH_NUMBERS.some((month) =>
      isMonthWithinMaxDate(year, month, maxDate)
    );
  }

  return QUARTER_OPTIONS.some((option) =>
    isQuarterWithinMaxDate(year, Number(option.value), maxDate)
  );
}

export function isMonthEndIsoDate(value: string): boolean {
  const parsed = parseIsoPeriod(value);
  return parsed != null && value === monthToIsoEnd(parsed.period, parsed.year);
}

type PickerSelectionState = {
  mode: MonthQuarterPickerMode;
  monthState: MonthSelectionState;
  quarterState: QuarterSelectionState;
};

/**
 * Parses an ISO string and initialises both month and quarter states so that
 * switching modes retains the correct year and cross-references sensibly.
 */
function parseIsoToStates(
  isoString: string,
  warnedDefaultValues: Set<string>
): PickerSelectionState | null {
  const parsed = parseIsoPeriod(isoString);
  if (!parsed || isoString !== monthToIsoEnd(parsed.period, parsed.year)) {
    if (!warnedDefaultValues.has(isoString)) {
      warnedDefaultValues.add(isoString);
      console.warn(
        `[MonthQuarterPicker] Ignoring invalid defaultValue "${isoString}".`
      );
    }
    return null;
  }

  const year = parsed.year;
  const month = parsed.period;
  const isQuarterEnd = month % 3 === 0;
  const quarter = isQuarterEnd ? quarterFromMonth(month) : null;
  return {
    mode: 'month',
    monthState: { month, year },
    quarterState: { quarter, year: isQuarterEnd ? year : null },
  };
}

const EMPTY_MONTH_STATE: MonthSelectionState = { month: null, year: null };
const EMPTY_QUARTER_STATE: QuarterSelectionState = {
  quarter: null,
  year: null,
};

function getEmptySelectionState(
  defaultYearToCurrent: boolean,
  currentYearInOptions: boolean,
  defaultYear: number
): PickerSelectionState {
  const year =
    defaultYearToCurrent && currentYearInOptions ? defaultYear : null;

  return {
    mode: 'month',
    monthState: { ...EMPTY_MONTH_STATE, year },
    quarterState: { ...EMPTY_QUARTER_STATE, year },
  };
}

function getInitialSelectionState({
  value,
  yearOptions,
  warnedDefaultValues,
  defaultYearToCurrent,
  currentYearInOptions,
  defaultYear,
}: {
  value?: string | null;
  yearOptions: YearOption[];
  warnedDefaultValues: Set<string>;
  defaultYearToCurrent: boolean;
  currentYearInOptions: boolean;
  defaultYear: number;
}): PickerSelectionState {
  const parsedState = value
    ? parseIsoToStates(value, warnedDefaultValues)
    : null;

  if (!parsedState) {
    return getEmptySelectionState(
      defaultYearToCurrent,
      currentYearInOptions,
      defaultYear
    );
  }

  const monthYearInOptions = yearOptions.some(
    (option) => option.value === String(parsedState.monthState.year)
  );
  const quarterYearInOptions =
    parsedState.quarterState.year !== null &&
    yearOptions.some(
      (option) => option.value === String(parsedState.quarterState.year)
    );

  return {
    mode: parsedState.mode,
    monthState: {
      ...parsedState.monthState,
      year: monthYearInOptions ? parsedState.monthState.year : null,
    },
    quarterState: {
      ...parsedState.quarterState,
      year: quarterYearInOptions ? parsedState.quarterState.year : null,
    },
  };
}

export function MonthQuarterPicker({
  value,
  defaultValue,
  onChange,
  disabled = false,
  loading = false,
  readOnly = false,
  clearable = true,
  defaultYearToCurrent = true,
  withinPortal = true,
  description,
  label,
  required,
  error,
  yearOptions = DEFAULT_YEAR_OPTIONS,
  maxDate,
  className,
  'data-testid': testId = 'month-quarter-picker',
}: MonthQuarterPickerProps) {
  const { t } = useTranslation();
  const locale = useFormattingLocale();
  const monthOptions = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { month: 'long' });
    return MONTH_NUMBERS.map((month) => ({
      label: formatter.format(new Date(2000, month - 1, 1)),
      value: String(month),
    }));
  }, [locale]);
  const warnedDefaultValues = useRef(new Set<string>());
  const lastEmittedValue = useRef<string | null | undefined>(undefined);
  const defaultYear = new Date().getFullYear();
  const currentYearInOptions = yearOptions.some(
    (option) => option.value === String(defaultYear)
  );
  const pickerValue = value !== undefined ? value : defaultValue;
  const initialSelectionState = getInitialSelectionState({
    value: pickerValue,
    yearOptions,
    warnedDefaultValues: warnedDefaultValues.current,
    defaultYearToCurrent,
    currentYearInOptions,
    defaultYear,
  });

  const MODE_OPTIONS = [
    { label: t('app.monthQuarterPicker.modeMonthly'), value: 'month' },
    { label: t('app.monthQuarterPicker.modeQuarterly'), value: 'quarter' },
  ];

  const [mode, setMode] = useState<MonthQuarterPickerMode>(
    () => initialSelectionState.mode
  );
  const [monthState, setMonthState] = useState<MonthSelectionState>(
    () => initialSelectionState.monthState
  );
  const [quarterState, setQuarterState] = useState<QuarterSelectionState>(
    () => initialSelectionState.quarterState
  );

  useEffect(() => {
    if (value === undefined) {
      return;
    }

    if (value === lastEmittedValue.current) {
      return;
    }

    const nextSelectionState = getInitialSelectionState({
      value,
      yearOptions,
      warnedDefaultValues: warnedDefaultValues.current,
      defaultYearToCurrent: false,
      currentYearInOptions,
      defaultYear,
    });

    setMode(nextSelectionState.mode);
    setMonthState(nextSelectionState.monthState);
    setQuarterState(nextSelectionState.quarterState);
  }, [value, yearOptions, currentYearInOptions, defaultYear]);

  function emitChange(newValue: string | null) {
    lastEmittedValue.current = newValue;
    onChange?.(newValue);
  }

  function getValueForActiveMode(
    currentMode: MonthQuarterPickerMode,
    ms: MonthSelectionState,
    qs: QuarterSelectionState
  ) {
    if (currentMode === 'month' && ms.month !== null && ms.year !== null) {
      const monthEnd = monthToIsoEnd(ms.month, ms.year);
      return isPeriodWithinMaxDate(monthEnd, maxDate) ? monthEnd : null;
    }

    if (currentMode === 'quarter' && qs.quarter !== null && qs.year !== null) {
      const quarterEnd = quarterToIsoEnd(qs.quarter, qs.year);
      return isPeriodWithinMaxDate(quarterEnd, maxDate) ? quarterEnd : null;
    }

    return null;
  }

  function emitActiveModeValue(
    currentMode: MonthQuarterPickerMode,
    ms: MonthSelectionState,
    qs: QuarterSelectionState
  ) {
    emitChange(getValueForActiveMode(currentMode, ms, qs));
  }

  function handleModeChange(newMode: string) {
    const m = newMode as MonthQuarterPickerMode;
    setMode(m);
    if (!readOnly) {
      emitActiveModeValue(m, monthState, quarterState);
    }
  }

  function handleMonthChange(month: string | null) {
    const newMs: MonthSelectionState = {
      ...monthState,
      month: month !== null ? Number(month) : null,
    };
    setMonthState(newMs);
    emitActiveModeValue('month', newMs, quarterState);
  }

  function handleMonthYearChange(year: string | null) {
    const newMs: MonthSelectionState = {
      ...monthState,
      year: year !== null ? Number(year) : null,
    };
    if (
      newMs.month !== null &&
      newMs.year !== null &&
      !isMonthWithinMaxDate(newMs.year, newMs.month, maxDate)
    ) {
      newMs.month = null;
    }
    setMonthState(newMs);
    emitActiveModeValue('month', newMs, quarterState);
  }

  function handleQuarterChange(quarter: string | null) {
    const newQs: QuarterSelectionState = {
      ...quarterState,
      quarter: quarter !== null ? Number(quarter) : null,
    };
    setQuarterState(newQs);
    emitActiveModeValue('quarter', monthState, newQs);
  }

  function handleQuarterYearChange(year: string | null) {
    const newQs: QuarterSelectionState = {
      ...quarterState,
      year: year !== null ? Number(year) : null,
    };
    if (
      newQs.quarter !== null &&
      newQs.year !== null &&
      !isQuarterWithinMaxDate(newQs.year, newQs.quarter, maxDate)
    ) {
      newQs.quarter = null;
    }
    setQuarterState(newQs);
    emitActiveModeValue('quarter', monthState, newQs);
  }

  const activeYear = mode === 'month' ? monthState.year : quarterState.year;
  const availableYearOptions = yearOptions.filter((option) =>
    isYearWithinMaxDate(Number(option.value), mode, maxDate)
  );
  const selectedMonthYear = monthState.year;
  const availableMonthOptions =
    selectedMonthYear == null
      ? monthOptions
      : monthOptions.filter((option) =>
          isMonthWithinMaxDate(selectedMonthYear, Number(option.value), maxDate)
        );
  const selectedQuarterYear = quarterState.year;
  const availableQuarterOptions =
    selectedQuarterYear == null
      ? QUARTER_OPTIONS
      : QUARTER_OPTIONS.filter((option) =>
          isQuarterWithinMaxDate(
            selectedQuarterYear,
            Number(option.value),
            maxDate
          )
        );

  return (
    <div className={clsx(styles['root'], className)} data-testid={testId}>
      <FormLabel
        description={description}
        label={label}
        required={required}
        disabled={disabled}
        error={error}
      />
      <SegmentedControl
        value={mode}
        options={MODE_OPTIONS}
        onChange={handleModeChange}
        disabled={disabled || loading}
      />
      <div className={styles['selects']}>
        <div
          data-testid={mode === 'quarter' ? 'quarter-select' : 'month-select'}
          className={styles['select-wrapper']}
        >
          {mode === 'quarter' ? (
            <SingleSelect
              data={availableQuarterOptions}
              value={
                quarterState.quarter !== null
                  ? String(quarterState.quarter)
                  : null
              }
              onChange={handleQuarterChange}
              disabled={disabled}
              readOnly={readOnly}
              loading={loading}
              clearable={clearable}
              withinPortal={withinPortal}
              placeholder={t('app.monthQuarterPicker.placeholderQuarter')}
              error={error}
            />
          ) : (
            <SingleSelect
              data={availableMonthOptions}
              value={
                monthState.month !== null ? String(monthState.month) : null
              }
              onChange={handleMonthChange}
              disabled={disabled}
              readOnly={readOnly}
              loading={loading}
              clearable={clearable}
              withinPortal={withinPortal}
              placeholder={t('app.monthQuarterPicker.placeholderMonth')}
              error={error}
            />
          )}
        </div>
        <div data-testid="year-select" className={styles['select-wrapper']}>
          <SingleSelect
            data={availableYearOptions}
            value={activeYear !== null ? String(activeYear) : null}
            onChange={
              mode === 'month' ? handleMonthYearChange : handleQuarterYearChange
            }
            disabled={disabled}
            readOnly={readOnly}
            loading={loading}
            clearable={clearable}
            withinPortal={withinPortal}
            placeholder={t('app.monthQuarterPicker.placeholderYear')}
            error={error ? true : undefined}
          />
        </div>
      </div>
    </div>
  );
}
